import { type MutationResult } from '@apollo/client';
import { type Dispatch, type SetStateAction, type FunctionComponent, useContext } from 'react';
import { type AddProductToCartMutation } from '../../../../__generated__/graphql-client-types';
import { StyledLink } from '../../../../components/common-components/link/styled-link.component';
import { QuantityInput } from '../../../../components/inputs';
import { AddToCartButtonFactory } from '../../../../components/product-components/add-to-cart-button/add-to-cart-button.component';
import { ShippingEligibility } from '../../../../components/product-components/shipping-eligibility/shipping-eligibility.component';
import { SqFtAddToCart } from '../../../../components/product-components/sqft-add-to-cart/sqft-add-to-cart.component';
import { SqFtCalculator } from '../../../../components/product-components/sqft-calculator/sqft-calculator.component';
import { VariantContext } from '../../../../contexts/variant/variant.context';
import { ADD_TO_CART_BUTTON_TYPE, type ButtonType } from '../../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { canAddToCart } from '../../../../helpers/product-helper/product.helper';
import { type PlaProductFamily } from '../../../../types/product.types';
import { cartQuantitySelector } from '../../pla-summary/pla-summary.css';

export type PlaControlsProps = {
	resetAddToCart: () => void;
	addToCartAction: ButtonType;
	addToCartButtonType: ADD_TO_CART_BUTTON_TYPE;
	isAddingToCart?: boolean;
	cartQuantity: number | undefined;
	setAndTrackCartQuantity: Dispatch<SetStateAction<number | undefined>>;
	setCartQuantity: Dispatch<SetStateAction<number | undefined>>;
	addToCartActionStatus: MutationResult<AddProductToCartMutation>;
	onAddToCartClick: () => void;
	units: {
		unitsValue: number;
		setUnits: Dispatch<SetStateAction<number>>;
	};
	product: PlaProductFamily;
	isRegionallyOutOfStock: boolean | '' | undefined;
	shouldDisplayMoreDetailsButton: boolean;
};

/**
 * Product Listing Controls Component
 */
export const PlaControls: FunctionComponent<PlaControlsProps> = ({
	isAddingToCart,
	onAddToCartClick,
	cartQuantity,
	setAndTrackCartQuantity,
	setCartQuantity,
	addToCartAction,
	addToCartActionStatus,
	units,
	product,
	isRegionallyOutOfStock,
	shouldDisplayMoreDetailsButton
}) => {
	const { currentVariant, selectedVariant } = useContext(VariantContext);
	const { unitsValue, setUnits } = units;
	const { isConfigurable, freightCost, url } = product;

	return (
		<div data-testid="pla-controls" className="mt4">
			<ShippingEligibility showZipCodeChanger={freightCost === 0} showOrderedBy={false} />
			<div className={'flex flex-wrap flex-nowrap-m items-center mt3 ml0-ns justify-center justify-start-ns'}>
				{canAddToCart(selectedVariant) && !isRegionallyOutOfStock && (
					<>
						<div className={`${cartQuantitySelector} w-50 pr1 pr0-ns mr3-ns mr2-m mw4-ns`}>
							<QuantityInput quantity={cartQuantity} setQuantity={setAndTrackCartQuantity} automationHook="cart-quantity" />
						</div>
						<div className={'w-50 pl1 pl0-ns w-40-ns pa0-ns mw5-ns bl-ns b--theme-grey-light'}>
							<div className={'mh3-ns mh2-m'}>
								<div className={'w-100'}>
									<AddToCartButtonFactory
										buttonType={addToCartAction}
										addToCartButtonType={ADD_TO_CART_BUTTON_TYPE.PLA}
										handleOnClick={onAddToCartClick}
										isConfigurable={isConfigurable}
										disabled={false}
										status={addToCartActionStatus}
										isBusy={isAddingToCart}
									/>
								</div>
							</div>
						</div>

						{/** See More Details  */}
						<div>
							{shouldDisplayMoreDetailsButton && (
								<div
									className={`input w-100-ns mt3 mt0-ns tc ${
										canAddToCart(selectedVariant) ? 'pl3-ns pl2-m bl-ns b--theme-grey-light' : ''
									}`}>
									<StyledLink url={`${url}?uid=${currentVariant.id}`}>
										<span className="nowrap">See More Details</span>
									</StyledLink>
								</div>
							)}
						</div>
					</>
				)}
			</div>
			{selectedVariant && selectedVariant.priceInfo.packaging && !isRegionallyOutOfStock && (
				<div className={`flex flex-column`}>
					<SqFtAddToCart
						className={`mt3`}
						priceInfo={selectedVariant.priceInfo}
						units={unitsValue}
						onClick={onAddToCartClick}
						pageName="Product-PLA"
						setQuantity={setCartQuantity}
					/>
					<SqFtCalculator className={`mt3`} onChange={setUnits} />
				</div>
			)}
		</div>
	);
};
